import React from 'react';
import styled from 'styled-components';
import { CHANGE_GLYPH, Icon } from '@repo/design-system';
import { semantic } from '@repo/design-tokens';
import type { ModelDiff } from '@repo/front-end-utils';
import { isEmptyModelDiff } from '@repo/front-end-utils';

const { roles } = semantic.status;

/**
 * Each group's glyph and colour (`onboarding-status-colours-launch`, R6): added and changed are just
 * changed, in mint; removed is a dead end, in the status red. Roles, never hues.
 */
export const CHANGE_LOOK = {
  added: { glyph: CHANGE_GLYPH.added, colour: roles.justChanged.mark },
  changed: { glyph: CHANGE_GLYPH.changed, colour: roles.justChanged.mark },
  removed: { glyph: CHANGE_GLYPH.removed, colour: roles.deadEnd.mark },
} as const;

/** The row's glyph, decorative: the group heading carries the meaning. */
const Marker: React.FC<{ change: keyof typeof CHANGE_LOOK }> = ({ change }) => (
  <span className="marker" aria-hidden>
    <Icon name={CHANGE_LOOK[change].glyph} size="sm" />
  </span>
);

interface Props {
  diff: ModelDiff;
  className?: string;
}

/**
 * The change list behind the success modal's "View changes" toggle (UPDATE only): what activating
 * this plan did to the previously-live model — entities added, removed, and (present on both sides)
 * changed in name, type, position or rotation, and floors moved up or down. Grouped, each group
 * shown only when non-empty.
 */
const ModelDiffView: React.FC<Props> = ({ diff, className }) => {
  if (isEmptyModelDiff(diff)) {
    return (
      <div className={className}>
        <p className="empty">No entity changes — the model matches what was already live.</p>
      </div>
    );
  }

  return (
    <div className={className}>
      {diff.added.length > 0 && (
        <section>
          <h5>Added ({diff.added.length})</h5>
          <ul>
            {diff.added.map(e => (
              <li key={e.bID} className="row row--added">
                <Marker change="added" />
                <span className="name">{e.name}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {diff.changed.length > 0 && (
        <section>
          <h5>Changed ({diff.changed.length})</h5>
          <ul>
            {diff.changed.map(e => (
              <li key={e.bID} className="row row--changed">
                <Marker change="changed" />
                <span className="body">
                  <span className="name">{e.name}</span>
                  <span className="detail">{e.changes.map(c => c.detail).join(', ')}</span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {diff.removed.length > 0 && (
        <section>
          <h5>Removed ({diff.removed.length})</h5>
          <ul>
            {diff.removed.map(e => (
              <li key={e.bID} className="row row--removed">
                <Marker change="removed" />
                <span className="name">{e.name}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default styled(ModelDiffView)`
  display: flex;
  flex-direction: column;
  gap: 0.9rem;

  .empty {
    margin: 0;
    font-size: 0.875rem;
    color: #6b6b6b;
  }

  section {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }

  h5 {
    margin: 0;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #6b6b6b;
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
  }

  .row {
    display: flex;
    gap: 0.5rem;
    align-items: flex-start;
    font-size: 0.875rem;
    line-height: 1.4;
  }

  .marker {
    display: inline-flex;
    flex-shrink: 0;
    width: 1rem;
    justify-content: center;
    margin-top: 0.1rem;
  }

  .row--added .marker {
    color: ${CHANGE_LOOK.added.colour};
  }

  .row--changed .marker {
    color: ${CHANGE_LOOK.changed.colour};
  }

  .row--removed .marker {
    color: ${CHANGE_LOOK.removed.colour};
  }

  .name {
    color: #1a1a1a;
  }

  .body {
    display: flex;
    flex-direction: column;
    gap: 0.05rem;
  }

  .detail {
    font-size: 0.8125rem;
    color: #6b6b6b;
  }
`;
